import { __decorate, __metadata, __param } from "tslib";
import { E_UserRole } from '@/apis/service/types';
import { Symbols } from '@/symbols';
import { getBeisenUser } from '@/utils/beisen';
import { inject, injectable } from 'inversify';
import _ from 'lodash';
let AccountInfoService = class AccountInfoService {
    constructor(accountApi, i18n, systemService, accountService) {
        this.accountApi = accountApi;
        this.i18n = i18n;
        this.systemService = systemService;
        this.accountService = accountService;
        this._accountTenants = [];
        this.accountBasic = {
            cas_user_id: '',
        };
        this.tenantsUsersBasic = {};
        this.tenantUsersSettingBasic = null;
    }
    $t(str) {
        return this.i18n.t(str);
    }
    getAccountBasic(id) {
        return this.accountApi.getAccountBasic(id).then(data => {
            this.accountBasic = _.merge(this.accountBasic, data);
            return data;
        });
    }
    getTenantsUsersBasic(params) {
        return this.accountApi.getTenantsUsersBasic(params).then(data => {
            this.tenantsUsersBasic = data;
            return data;
        });
    }
    set accountTenants(data) {
        this._accountTenants = data;
    }
    get accountTenants() {
        return this._accountTenants;
    }
    getAccountCurrentTenant(tenantId) {
        return _.find(this.accountTenants, ({ tenant_id }) => {
            if (tenant_id === tenantId)
                return true;
            if (!tenant_id && !tenantId)
                return true;
        });
    }
    getAccountTenants(account_id) {
        return this.accountApi.getAccountTenants(account_id).then(data => {
            this.accountTenants = data;
            return data;
        });
    }
    getCurrentTenantUserId(tenantId) {
        return _.find(this.accountTenants, ({ tenant_id }) => {
            if (tenantId === tenant_id)
                return true;
            if (!tenantId && !tenant_id)
                return true;
        }).id;
    }
    /**
     * 基本设置
     */
    getTenantUsersSettingBasic(params) {
        return this.accountApi.getTenantUsersSettingBasic(params).then(data => {
            this.tenantUsersSettingBasic = data;
            return data;
        });
    }
    accountBaseInfo(accountBaseInfo, tenantUser) {
        const emailIconConfig = this.accountService.getEmailIconConfig(accountBaseInfo, tenantUser);
        const mobileIconConfig = this.accountService.getMobileIconConfig(accountBaseInfo);
        return [
            { label: this.$t('backoffice.account.userId'), valueKey: 'id' },
            {
                label: this.$t('backoffice.account.mobileNumber'),
                valueKey: 'mobile_number',
                faIcon: mobileIconConfig.faIcon,
                iconClass: mobileIconConfig.class,
                tooltip: mobileIconConfig.tooltip,
            },
            {
                label: this.$t('backoffice.account.emailAddress'),
                valueKey: 'email',
                faIcon: emailIconConfig.faIcon,
                iconClass: emailIconConfig.class,
                tooltip: emailIconConfig.tooltip,
            },
            { label: this.$t('backoffice.account.job_title'), valueKey: 'job_role' },
            { label: this.$t('backoffice.account.industry'), valueKey: 'industry_type' },
            ...this.systemService.IsCn
                ? [
                    { label: this.$t('backoffice.account.name'), valueKey: 'nickname' }
                ]
                : [
                    { label: this.$t('backoffice.account.firstname'), valueKey: 'firstname' },
                    { label: this.$t('backoffice.account.lastname'), valueKey: 'lastname' }
                ],
            { label: this.$t('backoffice.common.country'), valueKey: 'country' },
            {
                label: this.$t('backoffice.account.language'),
                valueKey: 'language',
                filter: (val) => {
                    return val ? this.$t(`enum.E_Language.${val}`) : '-';
                },
            },
            {
                label: this.$t('backoffice.account.timezone'),
                valueKey: 'timezone_id',
            },
            { label: this.$t('backoffice.account.multiLogin'), valueKey: 'seats' }
        ];
    }
    accountPersonalInfo(accountBaseInfo) {
        return [
            { label: this.$t('backoffice.company.province'), valueKey: 'province' },
            { label: this.$t('backoffice.company.city'), valueKey: 'city' },
            { label: this.$t('backoffice.account.channel_code'), valueKey: 'channel_code' },
            { label: this.$t('backoffice.account.channel_source'), valueKey: 'channel_source' },
            { label: this.$t('backoffice.account.campaign_promotion'), valueKey: 'campaign_promotion' },
            { label: this.$t('backoffice.account.media_type'), valueKey: 'media_type' },
            { label: this.$t('backoffice.account.lead_source'), valueKey: 'lead_source' },
            { label: this.$t('backoffice.account.search_keywords'), valueKey: 'search_keywords' },
            { label: this.$t('backoffice.account.promotion_plan'), valueKey: 'promotion_plan' },
            { label: this.$t('backoffice.account.promotion_cell'), valueKey: 'promotion_cell' },
            {
                label: this.$t('backoffice.account.creat_from'),
                valueKey: 'created_from',
                filter: (val) => {
                    return val && this.i18n.te(`backoffice.enum.E_CreatedFrom.${val}`) ? this.$t(`backoffice.enum.E_CreatedFrom.${val}`) : val;
                },
            },
            {
                label: this.$t('backoffice.company.fromTrialApplication'),
                valueKey: 'is_trial_self_register',
                filter: (info) => {
                    return accountBaseInfo?.user_information_pojo?.self_trial ? this.$t('backoffice.common.yes') : this.$t('backoffice.common.no');
                },
            },
            { label: this.$t('backoffice.company.business_company_name_2'), valueKey: 'manual_company' }
        ];
    }
    accountOperatorInfo() {
        return [
            { label: this.$t('backoffice.account.createdAt'), valueKey: 'created_at_value' },
            { label: this.$t('backoffice.account.updatedAt'), valueKey: 'updated_at_value' },
            { label: this.$t('backoffice.account.created_source'), valueKey: 'created_source' }
        ];
    }
    /**
     * 用户租户信息
     */
    tenantsUsersBasicInfo(tenantsUsersBasic = {}) {
        const list = [
            { label: this.$t('backoffice.account.tenantUserId'), valueKey: 'id' },
            { label: this.$t('backoffice.common.paidType'), valueKey: 'charge_mode_value' },
            { label: this.$t('coreUtility.account.role'), valueKey: 'user_role' },
            { label: this.$t('backoffice.account.lastLogin'), valueKey: 'last_login_value' },
            { label: this.$t('backoffice.account.jobNumber'), valueKey: 'job_number' },
            { label: this.$t('backoffice.account.sales'), valueKey: 'sales_name' }
        ];
        return _.map(list, i => {
            return {
                ...i,
                value: this.getTenantsUsersItemValue(tenantsUsersBasic, i),
            };
        });
    }
    getTenantsUsersItemValue(tenantsUsersBasic = {}, item) {
        let itemValue = tenantsUsersBasic[item.valueKey];
        _.isNil(itemValue) && (itemValue = '-');
        if (item.valueKey === 'user_role') {
            return itemValue === E_UserRole.Admin ? this.$t('backoffice.company_users.admin') : this.$t('backoffice.account.General');
        }
        if (item.valueKey === 'sales_name') {
            const sales_pojo = tenantsUsersBasic.sales_pojo;
            return getBeisenUser({
                defaultName: sales_pojo?.sales_name,
                departmentName: sales_pojo?.department_name,
                employeeName: sales_pojo?.employee_name,
            });
        }
        return itemValue;
    }
    /**
     * 获取账号登录历史记录
     * @param params 查询参数
     * @returns 登录历史记录数据
     */
    getLoginHistory(params) {
        return this.accountApi.getLoginHistory(params);
    }
};
AccountInfoService = __decorate([
    injectable(),
    __param(0, inject(Symbols.AccountApi)),
    __param(1, inject(Symbols.I18n)),
    __param(2, inject(Symbols.SystemService)),
    __param(3, inject(Symbols.AccountService)),
    __metadata("design:paramtypes", [Object, Object, Object, Object])
], AccountInfoService);
export { AccountInfoService };
